import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { TrendingUp, Search, Eye, Users, AlertTriangle, BarChart3, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const getGradeColor = (letter) => {
  const colors = { A: 'bg-green-100 text-green-800', B: 'bg-blue-100 text-blue-800', C: 'bg-yellow-100 text-yellow-800', D: 'bg-orange-100 text-orange-800', F: 'bg-red-100 text-red-800' };
  return colors[letter] || 'bg-gray-100 text-gray-800';
};

const AcademicProgressTab = () => {
  const [rows, setRows] = useState([]);
  const [yearsAvailable, setYearsAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState('all');
  const [search, setSearch] = useState('');
  const [detailRow, setDetailRow] = useState(null);

  useEffect(() => { fetchOverview(); }, []);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/grades/admin/overview');
      setRows(res.data.rows || []);
      setYearsAvailable(res.data.years_available || []);
      if (res.data.years_available?.length > 0) setSelectedYear(res.data.years_available[0]);
    } catch (e) {
      toast.error('Error al cargar el progreso académico');
    } finally {
      setLoading(false);
    }
  };

  const grades = useMemo(() => [...new Set(rows.map(r => r.assigned_grade))].sort(), [rows]);
  const subjects = useMemo(() => [...new Set(rows.map(r => r.subject))].sort(), [rows]);
  const students = useMemo(() => {
    const map = new Map();
    rows.forEach(r => map.set(r.student_id, r.student_name));
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (selectedYear !== 'all' && r.academic_year !== selectedYear) return false;
      if (selectedGrade !== 'all' && r.assigned_grade !== selectedGrade) return false;
      if (selectedSubject !== 'all' && r.subject !== selectedSubject) return false;
      if (selectedStudent !== 'all' && r.student_id !== selectedStudent) return false;
      if (search.trim() && !r.student_name.toLowerCase().includes(search.trim().toLowerCase())) return false;
      return true;
    });
  }, [rows, selectedYear, selectedGrade, selectedSubject, selectedStudent, search]);

  const stats = useMemo(() => {
    const studentIds = new Set(filteredRows.map(r => r.student_id));
    const atRiskIds = new Set(filteredRows.filter(r => r.letter === 'D' || r.letter === 'F').map(r => r.student_id));
    const avg = filteredRows.length > 0
      ? filteredRows.reduce((sum, r) => sum + r.average_percentage, 0) / filteredRows.length
      : 0;
    return { totalStudents: studentIds.size, avgPercentage: avg, atRisk: atRiskIds.size };
  }, [filteredRows]);

  return (
    <div className="space-y-6" data-testid="academic-progress-tab">
      <div>
        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-indigo-600" /> Progreso Académico
        </h2>
        <p className="text-slate-500">Calificaciones de todos los estudiantes en todas las materias, para evaluación administrativa</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="modern-card" data-testid="ap-stat-students">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Estudiantes evaluados</p>
              <p className="text-2xl font-bold text-slate-800">{stats.totalStudents}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
          </CardContent>
        </Card>
        <Card className="modern-card" data-testid="ap-stat-average">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Promedio general</p>
              <p className="text-2xl font-bold text-slate-800">{stats.avgPercentage.toFixed(1)}%</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-emerald-600" /></div>
          </CardContent>
        </Card>
        <Card className="modern-card" data-testid="ap-stat-atrisk">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Estudiantes en riesgo</p>
              <p className="text-2xl font-bold text-slate-800">{stats.atRisk}</p>
              <p className="text-[11px] text-slate-400">Con al menos una materia D o F</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="modern-card">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar estudiante..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="ap-search-input"
              />
            </div>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger className="w-full lg:w-48" data-testid="ap-student-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estudiantes</SelectItem>
                {students.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-full lg:w-44" data-testid="ap-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los años</SelectItem>
                {yearsAvailable.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedGrade} onValueChange={setSelectedGrade}>
              <SelectTrigger className="w-full lg:w-40" data-testid="ap-grade-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los grados</SelectItem>
                {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
              <SelectTrigger className="w-full lg:w-44" data-testid="ap-subject-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las materias</SelectItem>
                {subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="modern-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div></div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <BookOpen className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              No hay calificaciones que coincidan con los filtros seleccionados.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estudiante</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Grado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Materia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Maestro</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Año</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Evaluaciones</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Promedio</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Detalle</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRows.map((r, i) => (
                      <tr key={`${r.student_id}-${r.subject}-${r.teacher_id}-${r.academic_year}`} className="hover:bg-gray-50" data-testid={`ap-row-${i}`}>
                        <td className="px-4 py-3 text-sm font-medium text-slate-800">{r.student_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.assigned_grade}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.subject}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.teacher_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{r.academic_year}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.evaluation_count}</td>
                        <td className="px-4 py-3">
                          <Badge className={getGradeColor(r.letter)}>{r.letter} ({r.average_percentage.toFixed(1)}%)</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => setDetailRow(r)} className="text-indigo-600 hover:text-indigo-800" data-testid={`ap-view-detail-${i}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile stacked list */}
              <div className="md:hidden divide-y divide-gray-200" data-testid="ap-mobile-list">
                {filteredRows.map((r, i) => (
                  <div key={`${r.student_id}-${r.subject}-${r.teacher_id}-${r.academic_year}`} className="p-4 space-y-1.5 cursor-pointer" onClick={() => setDetailRow(r)} data-testid={`ap-card-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800">{r.student_name}</p>
                      <Badge className={`${getGradeColor(r.letter)} shrink-0`}>{r.letter} ({r.average_percentage.toFixed(1)}%)</Badge>
                    </div>
                    <p className="text-xs text-slate-500">{r.assigned_grade} · {r.subject} · {r.teacher_name}</p>
                    <p className="text-xs text-slate-400">{r.academic_year} · {r.evaluation_count} evaluaciones</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detailRow} onOpenChange={(open) => !open && setDetailRow(null)}>
        <DialogContent className="max-w-lg" data-testid="ap-detail-dialog">
          {detailRow && (
            <>
              <DialogHeader>
                <DialogTitle>{detailRow.student_name} — {detailRow.subject}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500 -mt-2">Maestro: {detailRow.teacher_name} · Año: {detailRow.academic_year}</p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {detailRow.evaluations.map((ev, idx) => {
                  const mp = ev.max_points || (ev.grade_value > 10 ? 100 : 10);
                  const pct = (ev.grade_value / mp) * 100;
                  let badgeColor = 'bg-red-100 text-red-800';
                  if (pct >= 90) badgeColor = 'bg-green-100 text-green-800';
                  else if (pct >= 80) badgeColor = 'bg-blue-100 text-blue-800';
                  else if (pct >= 70) badgeColor = 'bg-yellow-100 text-yellow-800';
                  return (
                    <div key={ev.id || idx} className="flex items-start justify-between gap-2 p-2 rounded-lg bg-slate-50" data-testid={`ap-detail-eval-${idx}`}>
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800 break-words">{ev.description || 'Sin descripción'}</p>
                        {ev.notes && <p className="text-xs text-slate-500 break-words">{ev.notes}</p>}
                      </div>
                      <Badge className={`${badgeColor} shrink-0 whitespace-nowrap`}>{ev.grade_value}/{mp} ({pct.toFixed(0)}%)</Badge>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademicProgressTab;
